import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import {
  EscritoriosError,
  fetchMyEscritorio,
  patchMyEscritorio,
  type EscritorioRecord,
  type EscritoriosErrorReason,
} from '@/lib/escritorios';
import { mapEscritorioErrorReason } from '@/components/Members/error_messages';
import { Button, Input } from '@/components/ui';

function _normalizeCnpj(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 14);
}

interface DiffPayload {
  hasChange: boolean;
  payload: { nome?: string; cnpj?: string };
}

function _diffPayload(record: EscritorioRecord, nomeRaw: string, cnpjRaw: string): DiffPayload {
  const trimmedNome = nomeRaw.trim();
  const trimmedCnpj = _normalizeCnpj(cnpjRaw);
  const nomeChanged = trimmedNome !== record.nome && trimmedNome.length > 0;
  const cnpjChanged = trimmedCnpj !== (record.cnpj ?? '');
  return {
    hasChange: nomeChanged || cnpjChanged,
    payload: {
      ...(nomeChanged ? { nome: trimmedNome } : {}),
      ...(cnpjChanged ? { cnpj: trimmedCnpj } : {}),
    },
  };
}

interface EscritorioFormFieldsProps {
  nome: string;
  cnpj: string;
  readOnly: boolean;
  submitting: boolean;
  onNomeChange(value: string): void;
  onCnpjChange(value: string): void;
}

function EscritorioFormFields({
  nome,
  cnpj,
  readOnly,
  submitting,
  onNomeChange,
  onCnpjChange,
}: EscritorioFormFieldsProps) {
  return (
    <>
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm text-fg">{ptBR.escritorio.formNomeLabel}</span>
        <Input
          type="text"
          required
          maxLength={200}
          value={nome}
          onChange={(e) => onNomeChange(e.target.value)}
          placeholder={ptBR.escritorio.formNomePlaceholder}
          disabled={submitting || readOnly}
          data-testid="escritorio-nome-input"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm text-fg">{ptBR.escritorio.formCnpjLabel}</span>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={14}
          value={cnpj}
          onChange={(e) => onCnpjChange(_normalizeCnpj(e.target.value))}
          placeholder={ptBR.escritorio.formCnpjPlaceholder}
          disabled={submitting || readOnly}
          data-testid="escritorio-cnpj-input"
        />
        <span className="text-body-sm text-fg-muted">{ptBR.escritorio.formCnpjHint}</span>
      </label>
    </>
  );
}

export function EscritorioPage() {
  const apiClient = useApiClient();
  const { role, loading: roleLoading } = useCurrentRole();
  const [record, setRecord] = useState<EscritorioRecord | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await fetchMyEscritorio(apiClient);
      setRecord(r);
      setNome(r.nome);
      setCnpj(r.cnpj ?? '');
    } catch (err) {
      const reason: EscritoriosErrorReason =
        err instanceof EscritoriosError ? err.reason : 'unknown';
      setLoadError(mapEscritorioErrorReason(reason));
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isOwner = role === 'owner';
  const readOnly = !isOwner || roleLoading;

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    if (record === null) return;
    setActionError(null);
    setActionSuccess(null);

    const diff = _diffPayload(record, nome, cnpj);
    if (!diff.hasChange) {
      setActionError(ptBR.escritorio.errorEmpty);
      return;
    }

    setSubmitting(true);
    try {
      const updated = await patchMyEscritorio(apiClient, diff.payload);
      setRecord(updated);
      setNome(updated.nome);
      setCnpj(updated.cnpj ?? '');
      setActionSuccess(ptBR.escritorio.saveSuccess);
    } catch (err) {
      const reason: EscritoriosErrorReason =
        err instanceof EscritoriosError ? err.reason : 'unknown';
      setActionError(mapEscritorioErrorReason(reason));
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    if (record === null) return;
    setNome(record.nome);
    setCnpj(record.cnpj ?? '');
    setActionError(null);
    setActionSuccess(null);
  }

  return (
    <section
      aria-labelledby="escritorio-title"
      data-testid="escritorio-page"
      className="mx-auto max-w-3xl"
    >
      <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">
        {ptBR.escritorio.eyebrow}
      </p>
      <h1 id="escritorio-title" className="text-display-md text-fg">
        {ptBR.escritorio.pageTitle}
      </h1>
      <p className="mt-2 text-body-md text-fg-muted">{ptBR.escritorio.intro}</p>

      {loading ? (
        <p className="mt-8 text-body-md text-fg-muted" data-testid="escritorio-loading">
          {ptBR.members.listLoading}
        </p>
      ) : loadError !== null ? (
        <div
          className="mt-8 border border-danger bg-danger-50 px-4 py-3"
          data-testid="escritorio-load-error"
        >
          <p className="text-body-sm text-danger-700">{loadError}</p>
          <Button variant="secondary" type="button" onClick={refresh} className="mt-3">
            {ptBR.escritorio.loadErrorRetry}
          </Button>
        </div>
      ) : record !== null ? (
        <form
          onSubmit={handleSubmit}
          className="mt-8 flex flex-col gap-5"
          data-testid="escritorio-form"
        >
          <EscritorioFormFields
            nome={nome}
            cnpj={cnpj}
            readOnly={readOnly}
            submitting={submitting}
            onNomeChange={setNome}
            onCnpjChange={setCnpj}
          />

          {actionSuccess !== null ? (
            <p
              role="status"
              data-testid="escritorio-action-success"
              className="border border-success bg-success-50 px-3 py-2 text-body-sm text-success-700"
            >
              {actionSuccess}
            </p>
          ) : null}

          {actionError !== null ? (
            <p
              role="alert"
              data-testid="escritorio-action-error"
              className="border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
            >
              {actionError}
            </p>
          ) : null}

          {!readOnly ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                data-testid="escritorio-cancel"
              >
                {ptBR.escritorio.formCancel}
              </Button>
              <Button type="submit" disabled={submitting} data-testid="escritorio-submit">
                {submitting ? ptBR.escritorio.saving : ptBR.escritorio.formSave}
              </Button>
            </div>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
